'use client'

import { Suspense } from 'react'
import { useParams, useSearchParams } from 'next/navigation'
import clsx from 'clsx'
import Breadcrumbs from '@/components/Shared/Breadcrumbs'
import ArticleCard from '@/components/Shared/Card/ArticleCard'
import Pagination from '@/components/Shared/Pagination'
import Follow from '@/components/Shared/Follow'
import Loading from '@/components/Shared/Loading'
import DataFetchError from '@/components/Shared/DataFetchError'
import { CategoryService } from '@/lib/api/services/category'
import { ERROR_MESSAGES, EMPTY_STATE_MESSAGES } from '@/constants/messages'
import { useAsyncData } from '@/hooks/useAsyncData'
import styles from './styles.module.scss'

// 型定義
import type { Tag, SearchResult } from '@/lib/api/types/api'

interface TagWithDescription extends Tag {
  description?: string
}

interface StoriesResponse {
  records: SearchResult[]
  pageMeta: {
    currentPage: number
    totalPages: number
    totalContent?: number
  }
}

// カテゴリページデータ型
interface CategoryPageData {
  tag: TagWithDescription | null
  stories: StoriesResponse
}

function CategoryDetailPageContent() {
  const params = useParams()
  const searchParams = useSearchParams()
  const pageParam = searchParams.get('page')
  const currentPage = pageParam ? parseInt(pageParam) : 1

  // データ取得（useAsyncDataフック使用）
  const {
    data,
    isLoading: showLoading,
    error,
  } = useAsyncData<CategoryPageData>(
    async () => {
      // タグ情報を取得
      const tagData = await CategoryService.getTagById(params.Id as string)

      if (!tagData) {
        return { tag: null, stories: { records: [], pageMeta: { currentPage: 1, totalPages: 1 } } }
      }

      // ストーリー一覧を取得（tagSlugを使用）
      const storiesData = await CategoryService.getStoriesByTag(params.Id as string, currentPage)

      return {
        tag: tagData,
        stories: {
          records: storiesData.records || [],
          pageMeta: {
            currentPage: storiesData.pageMeta?.currentPage || currentPage,
            totalPages: storiesData.pageMeta?.totalPages || 1,
            totalContent: storiesData.pageMeta?.totalContent,
          },
        },
      }
    },
    [params.Id, currentPage],
    { enabled: !!params.Id },
  )

  const tag = data?.tag || null
  const stories = data?.stories || { records: [], pageMeta: { currentPage: 1, totalPages: 1 } }

  return (
    <div className='twoColumnLayout withBreadcrumb'>
      {showLoading ? (
        <div className={clsx('loadingWrapper', styles.categoryLoading)}>
          <Loading />
        </div>
      ) : error || !tag ? (
        <>
          <Breadcrumbs>
            <li>カテゴリー</li>
          </Breadcrumbs>
          <DataFetchError
            title={ERROR_MESSAGES.GENERIC}
            message={error?.message || EMPTY_STATE_MESSAGES.NOT_FOUND('カテゴリ')}
          />
        </>
      ) : (
        <>
          <Breadcrumbs used='twoColumnLayout'>
            <li>#{tag.name}</li>
          </Breadcrumbs>
          <div className='twoColumnContainer nonRightMargin'>
            <div className={styles.sectionWrapper}>
              <div className={styles.contentsHeader}>
                <div className={styles.sectionTitleSimple}>
                  <h1>#{tag.name}</h1>
                </div>
                <Follow followText='フォロー' type='category' item={tag} used='title' />
              </div>
              <div className='cardContainer'>
                {stories.records.length > 0 ? (
                  stories.records.map((story) => (
                    <ArticleCard
                      key={story.id}
                      article={story as any}
                      paramSrag={params.Id as string}
                    />
                  ))
                ) : (
                  <div className='noContent'>
                    <p className='noContentText'>
                      {EMPTY_STATE_MESSAGES.NO_CONTENT_IN('カテゴリ')}
                    </p>
                  </div>
                )}
              </div>
              {stories.pageMeta.totalPages > 1 && (
                <Pagination
                  currentPage={stories.pageMeta.currentPage}
                  totalPages={stories.pageMeta.totalPages}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default function CategoryDetailPageClient() {
  return (
    <Suspense fallback={<Loading />}>
      <CategoryDetailPageContent />
    </Suspense>
  )
}
